import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { siteData } from "@/lib/aht/pageData";
import { applyTheme, readThemeFromUrl, themeNames } from "@/theme/themes";

export function DraftPill() {
  const { ui } = siteData;
  return (
    <div
      className="fixed bottom-4 right-4 z-40 inline-flex h-8 items-center gap-2 rounded-full border border-hairline bg-paper px-3 text-[14px] font-medium text-ink"
      title={ui.draftHint}
      data-testid="draft-pill"
    >
      <span className="h-1.5 w-1.5 rounded-full bg-ink" aria-hidden="true" />
      {ui.draftLabel}
      <span className="sr-only">. {ui.draftHint}</span>
    </div>
  );
}

export function ThemePill() {
  const { ui } = siteData;
  const [theme, setTheme] = useState(readThemeFromUrl());
  const choose = (name) => {
    setTheme(applyTheme(name));
    const url = new URL(window.location.href);
    url.searchParams.set("theme", name);
    window.history.replaceState(null, "", url.toString());
  };
  return (
    <div
      className="fixed bottom-4 left-4 z-40 flex items-center gap-1 rounded-full border border-hairline bg-paper p-1 pl-4"
      role="group"
      aria-label={ui.themePill.label}
      data-testid="theme-pill"
      data-keynav-ignore=""
    >
      <span className="mr-2 text-[14px] text-graphite">{ui.themePill.label}</span>
      {themeNames.map((n) => (
        <button
          key={n}
          type="button"
          onClick={() => choose(n)}
          aria-pressed={theme === n}
          className={`h-11 rounded-full px-4 text-[14px] font-medium ${
            theme === n ? "bg-ink text-paper" : "text-ink hover:bg-mist"
          }`}
          data-testid={`theme-pill-${n}`}
        >
          {ui.themePill.options[n]}
        </button>
      ))}
    </div>
  );
}

// Vimeo player in a modal. Only rendered when agent.videoUrl is set.
export function VideoModal({ open, onClose, url, title, closeLabel }) {
  useEffect(() => {
    if (!open) return undefined;
    const root = document.documentElement;
    root.setAttribute("data-overlay-open", "");
    root.style.overflow = "hidden";
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => {
      root.removeAttribute("data-overlay-open");
      root.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  const id = url ? (String(url).match(/vimeo\.com\/(?:video\/)?(\d+)/) || [])[1] : null;
  const src = id ? `https://player.vimeo.com/video/${id}?autoplay=1&dnt=1` : url;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          role="dialog"
          aria-modal="true"
          aria-label={title}
          className="fixed inset-0 z-[80] flex items-center justify-center bg-ink p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          onClick={onClose}
          data-testid="video-modal"
        >
          <div className="relative w-full max-w-[1120px]" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              onClick={onClose}
              autoFocus
              className="absolute -top-14 right-0 flex h-11 w-11 items-center justify-center rounded-full text-paper"
              aria-label={closeLabel}
              data-testid="video-modal-close-button"
            >
              <X size={24} aria-hidden="true" />
            </button>
            <div className="aspect-video w-full overflow-hidden rounded-tile bg-ink-raised">
              <iframe src={src} title={title} className="h-full w-full" allow="autoplay; fullscreen; picture-in-picture" allowFullScreen />
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
